/**
 * InstallPrompt — offers the user a one-click "Install Amal ERP" option when
 * the browser fires `beforeinstallprompt` (Chrome / Edge / Brave on desktop +
 * Android). Once the user installs OR dismisses, we remember that choice in
 * localStorage and don't bother them again.
 *
 * Renders nothing on browsers that don't fire the event (Safari, Firefox) or
 * when the app is already installed (display-mode: standalone).
 *
 * Pure addition. Mounted near the top of App. Zero impact when no install is
 * possible.
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiDownload, FiX } from 'react-icons/fi';

const DISMISS_KEY = 'amal.installPromptDismissed';
const DISMISS_TTL_DAYS = 30; // re-ask after a month if dismissed

const wasRecentlyDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    const daysSince = (Date.now() - at) / (1000 * 60 * 60 * 24);
    return daysSince < DISMISS_TTL_DAYS;
  } catch { return false; }
};

const isAlreadyInstalled = () => {
  // PWA running in standalone window — no need to prompt
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari has its own flag
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
};

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isAlreadyInstalled()) return;
    if (wasRecentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch { /* user dismissed the native prompt — fine */ }
    setVisible(false);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <Toast role="dialog" aria-label="Install Amal ERP">
      <IconWrap><FiDownload size={18} /></IconWrap>
      <Body>
        <Title>Install Amal ERP</Title>
        <Sub>Add to your desktop for one-click access.</Sub>
      </Body>
      <InstallBtn onClick={install}>Install</InstallBtn>
      <DismissBtn onClick={dismiss} aria-label="Dismiss">
        <FiX size={14} />
      </DismissBtn>
    </Toast>
  );
};

export default InstallPrompt;

// ───────────── styles ─────────────
const Toast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 9999;
  max-width: 340px;
  font-size: 13px;
`;

const IconWrap = styled.div`
  background: #800020;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-weight: 600;
  color: #1A1A1A;
  font-size: 13px;
`;

const Sub = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`;

const InstallBtn = styled.button`
  background: #800020;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #66001a; }
`;

const DismissBtn = styled.button`
  background: transparent;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  &:hover { background: #f5f5f5; color: #1A1A1A; }
`;
